// Storage & Processing room — the in-game Tech & Build Directory.
//
// Three screens (Search / Detail triptych / Crafting) ported from the
// tech_tree_v17.html spec, but driven by server-authoritative room state
// (cargo, craftQueue, autoCraft, wares, ownedUpgrades) and emitting socket
// actions rather than mutating local state. Data is loaded once from
// /config/tech-tree-data.json and indexed via the shared tech-tree module.

import type { RoomState, StorageActionPayload } from '../../shared/types.js';
import {
  buildTechTreeIndex,
  boxStatus,
  type CargoBoxStatus,
  type TechNode,
  type TechTreeData,
  type TechTreeIndex
} from '../../shared/techTree.js';
import { state as controllerState } from '../controllerState.js';
import type { ControllerNetwork } from '../controllerNetwork.js';
import { t } from '../i18n.js';
import { UPGRADE_CRAFT_LOAD_MULTIPLIER } from '../../shared/constants.js';

type CreateStorageRoomControllerOptions = {
  controls: HTMLDivElement | null;
  /** Lower-left controller zone — hosts the filter rail. */
  leftBottom: HTMLDivElement | null;
  /** Lower-right controller zone — hosts the Search / Crafting nav buttons. */
  rightAux: HTMLDivElement | null;
  getControllerNetwork: () => ControllerNetwork | null;
};

type ScreenName = 'search' | 'detail' | 'crafting';

// Presentation maps (client-only; mirror the spec harness).
const CAT_LABEL: Record<string, string> = {
  propulsion: 'Propulsion',
  life_support: 'Life Support',
  hull: 'Hull',
  offense_ammo: 'Offense · Ammo',
  offense_upgrade: 'Offense · Systems',
  drones: 'Drones',
  utility_consumable: 'Utility · Consumables',
  utility_upgrade: 'Utility · Systems'
};
const CAT_ORDER = ['propulsion', 'life_support', 'hull', 'offense_ammo', 'offense_upgrade', 'drones', 'utility_consumable', 'utility_upgrade'];
const CAT_COLOR: Record<string, string> = {
  propulsion: '#4fc3f7',
  life_support: '#66bb6a',
  hull: '#185FA5',
  offense_ammo: '#ef5350',
  offense_upgrade: '#ba68c8',
  drones: '#ffd54f',
  utility_consumable: '#26c6da',
  utility_upgrade: '#9575cd'
};

const SVG = (path: string, color?: string): string =>
  `<svg class="ico" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="${color || 'currentColor'}" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round">${path}</svg>`;
const IC = {
  hammer: SVG('<path d="m15 12-8.5 8.5c-.83.83-2.17.83-3 0a2.12 2.12 0 0 1 0-3L12 9"/><path d="M17.64 15 22 10.64"/><path d="m20.91 11.7-1.25-1.25c-.6-.6-.93-1.4-.93-2.25v-.86L16.01 4.6a5.56 5.56 0 0 0-3.94-1.64H9l.92.82A6.18 6.18 0 0 1 12 8.4v1.56l2 2h.86c.85 0 1.65.34 2.25.93l1.25 1.25"/>'),
  x: SVG('<path d="M18 6 6 18"/><path d="m6 6 12 12"/>', '#ef5350'),
  dollar: SVG('<line x1="12" x2="12" y1="2" y2="22"/><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/>'),
  auto: SVG('<path d="M3 12a9 9 0 0 1 9-9 9.75 9.75 0 0 1 6.74 2.74L21 8"/><path d="M21 3v5h-5"/><path d="M21 12a9 9 0 0 1-9 9 9.75 9.75 0 0 1-6.74-2.74L3 16"/><path d="M8 16H3v5"/>'),
  plus: SVG('<path d="M5 12h14"/><path d="M12 5v14"/>')
};

const escapeHtml = (value: string): string =>
  value.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');

let techIndex: TechTreeIndex | null = null;
let techLoadStarted = false;

// ---- transient top-of-screen toast (crafting feedback) ----
let toastEl: HTMLDivElement | null = null;
let toastTimer: number | null = null;

const showToast = (message: string, kind: 'ok' | 'warn' = 'ok'): void => {
  if (typeof document === 'undefined') return;
  if (!toastEl) {
    toastEl = document.createElement('div');
    toastEl.className = 'tech-toast';
    document.body.appendChild(toastEl);
  }
  toastEl.textContent = message;
  toastEl.classList.toggle('warn', kind === 'warn');
  // Restart the fade-in/out animation even on rapid repeat taps.
  toastEl.classList.remove('show');
  void toastEl.offsetWidth; // force reflow so the transition replays
  toastEl.classList.add('show');
  if (toastTimer !== null) window.clearTimeout(toastTimer);
  toastTimer = window.setTimeout(() => {
    toastEl?.classList.remove('show');
    toastTimer = null;
  }, 1600);
};

/**
 * Hold space used, in spots (quantity x storagePerUnit - the same rule the server enforces).
 * Returns null until the tech directory has loaded so callers can fall back to a raw unit count.
 */
export const getStorageSpotsUsed = (cargo: Record<string, number>): number | null => {
  if (!techIndex) return null;
  let used = 0;
  for (const [id, qty] of Object.entries(cargo)) used += qty * (techIndex.nodes[id]?.storagePerUnit ?? 1);
  return used;
};

const loadTechIndex = (onReady: () => void): void => {
  if (techIndex || techLoadStarted) {
    if (techIndex) onReady();
    return;
  }
  techLoadStarted = true;
  fetch('/config/tech-tree-data.json')
    .then((res) => res.json() as Promise<TechTreeData>)
    .then((data) => {
      techIndex = buildTechTreeIndex(data);
      onReady();
    })
    .catch(() => {
      techLoadStarted = false; // allow a retry on next render
    });
};

type FilterDef = { id: string; label: string; dot?: string; test: (node: TechNode) => boolean };
type FilterGroup = { grp: string; items: FilterDef[] };

export const createStorageRoomController = ({ controls, leftBottom, rightAux, getControllerNetwork }: CreateStorageRoomControllerOptions) => {
  const view = { screen: 'search' as ScreenName, selected: null as string | null, filter: 'all', searchText: '', filterOwned: false, filterCraftable: false };
  let root: HTMLDivElement | null = null;
  // References to buttons rendered into the outer controller zones (updated incrementally).
  const filterButtons = new Map<string, { btn: HTMLButtonElement; count: HTMLSpanElement }>();
  const overlayButtons: { owned?: HTMLButtonElement; craftable?: HTMLButtonElement } = {};
  const navButtons: { search?: HTMLButtonElement; crafting?: HTMLButtonElement; qcnt?: HTMLSpanElement } = {};

  // Ammo (rail / mini / shotgun / harpoon-bolt rounds) is hidden from the room everywhere.
  const isAmmoHidden = (n: TechNode): boolean => n.type === 'product' && n.category === 'offense_ammo';

  // ---- data helpers (read server-authoritative room state) ----
  const idx = (): TechTreeIndex => techIndex as TechTreeIndex;
  const node = (id: string): TechNode | undefined => techIndex?.nodes[id];
  const roomState = (): RoomState | null => controllerState.lastRoomState;
  const inv = (id: string): number => roomState()?.cargo[id] ?? 0;
  const isOwned = (id: string): boolean => roomState()?.ownedUpgrades.includes(id) ?? false;
  const isAuto = (id: string): boolean => roomState()?.autoCraft.includes(id) ?? false;
  const isWare = (id: string): boolean => roomState()?.wares.includes(id) ?? false;
  const queue = () => roomState()?.craftQueue ?? [];

  const FILTERS: FilterGroup[] = [
    { grp: 'Library', items: [
      { id: 'all', label: 'All items', test: () => true },
      { id: 'cargo', label: 'In cargo', test: (n) => inv(n.id) > 0 }
    ] },
    { grp: 'Type', items: [
      { id: 'raw', label: 'Raw materials', test: (n) => n.type === 'raw' },
      { id: 'refined', label: 'Refined', test: (n) => n.type === 'refined' },
      { id: 'consumable', label: 'Consumables', test: (n) => n.type === 'product' && n.kind === 'consumable' },
      { id: 'upgrade', label: 'Upgrades', test: (n) => n.type === 'product' && n.kind === 'upgrade' }
    ] }
  ];
  const filterDef = (id: string): FilterDef => {
    for (const g of FILTERS) for (const f of g.items) if (f.id === id) return f;
    return FILTERS[0].items[0];
  };
  const countFor = (f: FilterDef): number => {
    let c = 0;
    for (const id of idx().order) {
      const n = idx().nodes[id];
      if (!isAmmoHidden(n) && f.test(n)) c += 1;
    }
    return c;
  };
  const isUpgrade = (n: TechNode): boolean => n.type === 'product' && n.kind === 'upgrade';
  const isCraftable = (n: TechNode): boolean => {
    if (n.type === 'raw') return false;
    // Upgrades are one-time: nothing to build once installed, in the hold, or already in the queue.
    if (isUpgrade(n) && (isOwned(n.id) || inv(n.id) > 0 || queue().some((job) => job.id === n.id))) return false;
    return Object.entries(n.inputs).every(([id, qty]) => inv(id) >= qty);
  };

  const visibleNodes = (): TechNode[] => {
    const f = filterDef(view.filter);
    const q = view.searchText.trim().toLowerCase();
    const arr = idx().order
      .map((id) => idx().nodes[id])
      .filter((n) => {
        if (isAmmoHidden(n)) return false;
        if (!(q ? n.name.toLowerCase().includes(q) : f.test(n))) return false;
        if (view.filterOwned && !isOwned(n.id)) return false;
        if (view.filterCraftable && !isCraftable(n)) return false;
        return true;
      });
    arr.sort((a, b) => a.name.localeCompare(b.name));
    return arr;
  };

  // ---- presentation helpers ----
  const entColor = (n: TechNode): string => {
    if (n.type === 'product') return CAT_COLOR[n.category ?? ''] ?? 'var(--refined)';
    if (n.type === 'refined') return 'var(--refined)';
    return n.rarity === 'common' ? 'var(--common)' : n.rarity === 'uncommon' ? 'var(--uncommon)' : 'var(--rare)';
  };
  const typeLabel = (n: TechNode): string => {
    if (n.type === 'raw') return `${n.rarity} raw`;
    if (n.type === 'refined') return 'refined';
    return `${CAT_LABEL[n.category ?? ''] ?? ''} · ${n.kind}`;
  };
  const nm = (id: string): string => node(id)?.name ?? id;
  const cap1 = (s: string): string => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s);
  /** "10 min" for long builds, "45 s" for short ones. */
  const fmtDuration = (ms: number): string => {
    const seconds = Math.round(ms / 1000);
    return seconds >= 120 ? `${Math.round(seconds / 60)} min` : `${seconds} s`;
  };
  const fmt = (seconds: number): string => {
    const s = Math.max(0, Math.round(seconds));
    const m = Math.floor(s / 60);
    const x = s % 60;
    return `${m}:${x < 10 ? '0' : ''}${x}`;
  };

  const statusView = (id: string, need: number | null = null): { cls: string; pill: string } => {
    const n = node(id);
    const have = inv(id);
    const st: CargoBoxStatus = boxStatus(n, have, need, isOwned(id));
    if (st === 'owned') return { cls: 'owned', pill: '<span class="ecargo ok">✓</span>' };
    if (st === 'none') return { cls: 'empty', pill: '' };
    if (st === 'short') return { cls: 'short', pill: `<span class="ecargo short">${have}</span>` };
    return { cls: 'have', pill: `<span class="ecargo have">${have}</span>` };
  };

  const entBox = (id: string, opts: { qty?: string; meta?: string; need?: number } = {}): string => {
    const n = node(id);
    if (!n) {
      // Tolerated dangling input id (data §7): render a plain greyed box.
      const q = opts.qty ? `<span class="eqx">${escapeHtml(opts.qty)}</span>` : '';
      return `<div class="entrow">${q}<div class="ent empty" style="cursor:default"><span class="enm">${escapeHtml(id)}</span><span class="emeta">unknown</span></div></div>`;
    }
    const st = statusView(id, opts.need ?? null);
    const q = opts.qty ? `<span class="eqx">${escapeHtml(opts.qty)}</span>` : '';
    const meta = opts.meta ? `<span class="emeta">${escapeHtml(opts.meta)}</span>` : '';
    return `<div class="entrow">${q}<button class="ent ${st.cls}" data-go="${id}" style="box-shadow:inset 4px 0 0 ${entColor(n)}"><span class="etext"><span class="enm">${escapeHtml(n.name)}</span>${meta}</span>${st.pill}</button></div>`;
  };

  // Sum of remaining ms for all jobs up to and including the first job of `id`.
  const craftBar = (id: string): string => {
    const q = queue();
    const headIdx = q.findIndex((j) => j.id === id);
    if (headIdx < 0) return '';
    const jobsOfId = q.filter((j) => j.id === id).length;
    let etaMs = 0;
    for (let i = 0; i <= headIdx; i += 1) etaMs += q[i].remainingMs;
    const active = headIdx === 0;
    const head = q[headIdx];
    const pct = active && head.totalMs > 0 ? ((head.totalMs - head.remainingMs) / head.totalMs) * 100 : 0;
    return `<div class="cbar"><div class="cbar-main"><div class="cbar-top"><span class="cbar-lbl">${active ? 'Crafting now' : 'Queued'}${jobsOfId > 1 ? ` · ${jobsOfId} in queue` : ''}</span><span class="cbar-time">${fmt(etaMs / 1000)}</span></div><div class="qprog"><i style="width:${pct}%"></i></div></div><button class="cbar-x" data-act="cancelAll" data-id="${id}">${IC.x}</button></div>`;
  };

  // ---- screen renderers (return inner HTML for their container) ----
  const renderSearchList = (): string => {
    const arr = visibleNodes();
    if (!arr.length) return '<div class="emptymsg">Nothing matches. Clear the search or pick another filter.</div>';
    return arr.map((n) => entBox(n.id, { meta: typeLabel(n) })).join('');
  };

  const renderLeftPane = (): string => {
    const n = view.selected ? node(view.selected) : undefined;
    if (!n) return '';
    if (n.type === 'raw') return '<div class="hint">Raw material — mined in-world. No prerequisites.</div>';
    let h = '';
    if (!Object.keys(n.inputs).length) h += '<div class="hint">No inputs — produced directly.</div>';
    for (const k of Object.keys(n.inputs)) h += entBox(k, { qty: `${n.inputs[k]}×`, need: n.inputs[k] });
    if (n.catalysts.length) {
      h += n.catalysts.map((c) => `<div class="entrow"><span class="eqx"></span><div class="ent" style="cursor:default;border-style:dashed"><span class="edot" style="background:var(--heat)"></span><span class="enm">${escapeHtml(c)}</span><span class="ect">catalyst</span></div></div>`).join('');
    }
    return h;
  };

  const renderCenterPane = (): string => {
    const id = view.selected;
    const n = id ? node(id) : undefined;
    if (!id || !n) return '';
    let h = `<div class="ent title" style="box-shadow:inset 5px 0 0 ${entColor(n)}"><span class="enm">${escapeHtml(n.name)}</span>${statusView(id).pill}</div>`;
    if (n.type === 'refined' && n.coProduct) h += entBox(n.coProduct, { meta: 'co-product' });
    h += craftBar(id);
    // Ingredients block (was left pane)
    if (n.type === 'raw') {
      h += '<div class="hint">Raw material — mined in-world. No prerequisites.</div>';
    } else if (!Object.keys(n.inputs).length) {
      h += '<div class="hint">No inputs — produced directly.</div>';
    } else {
      h += '<div class="sec">Ingredients</div>';
      for (const k of Object.keys(n.inputs)) h += entBox(k, { qty: `${n.inputs[k]}×`, need: n.inputs[k] });
      if (n.catalysts.length) {
        h += n.catalysts.map((c) => `<div class="entrow"><span class="eqx"></span><div class="ent" style="cursor:default;border-style:dashed"><span class="edot" style="background:var(--heat)"></span><span class="enm">${escapeHtml(c)}</span><span class="ect">catalyst</span></div></div>`).join('');
      }
    }
    h += '<div class="sec">Actions</div>';
    if (n.type !== 'raw') {
      if (isUpgrade(n)) {
        // One-time items: a single build, with a clear warning about the cost of running it.
        const cannotBuild = isOwned(id) || inv(id) > 0 || queue().some((job) => job.id === id);
        h += `<div class="arow one"><button class="cbtn primary" data-act="craft1" data-id="${id}"${cannotBuild ? ' disabled' : ''}>${IC.hammer}Build upgrade · ${fmtDuration(n.craftTimeMs)}</button></div>`;
        h += `<div class="hint">While building, the processor draws ${UPGRADE_CRAFT_LOAD_MULTIPLIER}× its normal power and oxygen.</div>`;
      } else {
        h += `<div class="arow"><button class="cbtn primary" data-act="craft1" data-id="${id}">${IC.hammer}Craft 1</button><button class="cbtn" data-act="craft10" data-id="${id}">${IC.hammer}Craft 10</button><button class="cbtn auto ${isAuto(id) ? 'on' : ''}" data-act="auto" data-id="${id}">${IC.auto}${isAuto(id) ? 'Auto ON' : 'Auto-craft'}</button></div>`;
      }
    }
    h += `<div class="arow"><button class="cbtn" data-act="dump1" data-id="${id}">${IC.x}Dump 1</button><button class="cbtn" data-act="dump10" data-id="${id}">${IC.x}Dump 10</button><button class="cbtn" data-act="dump100" data-id="${id}">${IC.x}Dump 100</button></div>`;
    h += `<div class="arow"><button class="cbtn wares ${isWare(id) ? 'on' : ''}" data-act="wares" data-id="${id}">${IC.dollar}${isWare(id) ? 'In wares ✓' : 'Add to wares'}</button></div>`;
    const rows: Array<[string, string]> = [];
    rows.push(['Type', n.type === 'raw' ? 'Raw material' : n.type === 'refined' ? 'Refined intermediate' : `${CAT_LABEL[n.category ?? ''] ?? ''} · ${n.kind}`]);
    if (n.type === 'raw') {
      rows.push(['Rarity', cap1(n.rarity ?? '')]);
      if (n.sym) rows.push(['Symbol', n.sym]);
    }
    if (n.type === 'product') rows.push(['Tier', `T${n.tier}`]);
    if (n.desc) rows.push(['Description', n.desc]);
    rows.push(['In cargo', String(inv(id))]);
    rows.push(['Storage / unit', `${n.storagePerUnit} spot${n.storagePerUnit === 1 ? '' : 's'}`]);
    rows.push(['Est. value / unit', String(n.estValue)]);
    if (n.type !== 'raw') rows.push(['Craft time', fmtDuration(n.craftTimeMs)]);
    if (isWare(id)) rows.push(['Wares', 'In sale list']);
    h += `<div class="sec">Details</div><div class="dl">${rows.map((r) => `<div class="row"><div class="k">${escapeHtml(r[0])}</div><div class="v">${escapeHtml(r[1])}</div></div>`).join('')}</div>`;
    // Used-in block (was right pane)
    const cons = (idx().consumers[id] ?? [])
      .filter((cid) => { const cn = node(cid); return !cn || !isAmmoHidden(cn); })
      .sort((a, b) => nm(a).localeCompare(nm(b)));
    if (cons.length) {
      h += '<div class="sec">Used in</div>';
      h += cons.map((cid) => entBox(cid)).join('');
    }
    // Crafted upgrades sit in the hold until they are applied here.
    if (isUpgrade(n)) {
      h += '<div class="sec">Apply</div>';
      if (isOwned(id)) {
        h += '<div class="hint">Installed ✓ — this upgrade is already active on the submarine.</div>';
      } else if (inv(id) > 0) {
        h += `<div class="arow one"><button class="cbtn primary" data-act="apply" data-id="${id}">${IC.hammer}Apply upgrade</button></div><div class="hint">Installs the upgrade permanently and uses up the item.</div>`;
      } else {
        h += `<div class="hint">None in the hold. Build one (${fmtDuration(n.craftTimeMs)}) or buy it at a base.</div>`;
      }
    }
    return h;
  };

  const renderRightPane = (): string => {
    const id = view.selected;
    if (!id) return '';
    const cons = (idx().consumers[id] ?? [])
      .filter((cid) => { const cn = node(cid); return !cn || !isAmmoHidden(cn); })
      .sort((a, b) => nm(a).localeCompare(nm(b)));
    if (!cons.length) return `<div class="hint">Nothing is crafted directly from ${escapeHtml(nm(id))} — it's an end product.</div>`;
    return cons.map((cid) => entBox(cid)).join('');
  };

  const renderCraftingScreen = (): string => {
    const q = queue();
    const auto = roomState()?.autoCraft ?? [];
    let h = `<div class="craftwrap"><div class="qsec">Build queue · ${q.length}</div>`;
    if (!q.length) h += '<div class="hint">Queue is empty. Open an item and hit Craft, or turn on Auto-craft.</div>';
    let etaMs = 0;
    q.forEach((j, i) => {
      const n = node(j.id);
      etaMs += j.remainingMs;
      const active = i === 0;
      const pct = active && j.totalMs > 0 ? ((j.totalMs - j.remainingMs) / j.totalMs) * 100 : 0;
      const color = n ? entColor(n) : 'var(--line)';
      h += `<div class="qjob ${active ? 'active' : ''}" data-go="${j.id}" style="box-shadow:inset 4px 0 0 ${color}">
        <div class="qbody"><div class="qn">${escapeHtml(n?.name ?? j.id)}</div><div class="qmeta">${active ? 'crafting now' : 'queued'} · ${fmtDuration(j.totalMs)} each</div>${active ? `<div class="qprog"><i style="width:${pct}%"></i></div>` : ''}</div>
        <div class="qright"><span class="qtime">${fmt(etaMs / 1000)}</span><button class="qx" data-job="${j.jobId}">✕</button></div></div>`;
    });
    h += `<div class="qsec" style="margin-top:20px">Auto-craft · ${auto.length}</div>`;
    if (!auto.length) h += '<div class="hint">Nothing on auto. Open an item and tap Auto-craft to keep it topped up — it re-queues itself whenever its last batch finishes.</div>';
    for (const id of auto) {
      const n = node(id);
      h += `<div class="qjob" data-go="${id}" style="box-shadow:inset 4px 0 0 ${n ? entColor(n) : 'var(--line)'}"><div class="qbody"><div class="qn">${escapeHtml(n?.name ?? id)}</div><div class="qmeta">auto · ~1 every ${n ? Math.round(n.craftTimeMs / 1000) : 60}s</div></div><div class="qright"><button class="qx" data-auto="${id}">Stop</button></div></div>`;
    }
    h += '</div>';
    return h;
  };

  // ---- shell + sync ----
  // The status bar / nav buttons / filter rail live in the outer controller zones
  // (left-bottom + right-aux); the middle zone holds only the single-column screens.
  const shellHtml = (): string => `
    <div class="tech-room">
      <div class="stage">
        <div class="screen" data-screen-panel="search">
          <main class="flist tech-single">
            <div class="search"><i class="ic">⌕</i><input data-el="searchIn" type="text" placeholder="Search materials…" autocomplete="off"></div>
            <div class="listhead"><h2 data-el="listTitle">All items</h2><span class="n" data-el="listCount"></span></div>
            <div data-el="listBody"></div>
          </main>
        </div>
        <div class="screen" data-screen-panel="detail">
          <section class="pane detail-single" data-el="paneCenter"></section>
        </div>
        <div class="screen" data-screen-panel="crafting" data-el="craftingScreen"></div>
      </div>
    </div>`;

  const el = (name: string): HTMLElement | null => root?.querySelector(`[data-el="${name}"]`) ?? null;

  const applyScreenVisibility = (): void => {
    root?.querySelectorAll<HTMLElement>('[data-screen-panel]').forEach((panel) => {
      panel.classList.toggle('active', panel.dataset.screenPanel === view.screen);
    });
    // Nav buttons live in the right-aux zone; "search" stays lit while on the detail screen.
    navButtons.search?.classList.toggle('on', view.screen === 'search' || view.screen === 'detail');
    navButtons.crafting?.classList.toggle('on', view.screen === 'crafting');
  };

  const updateNav = (): void => {
    if (navButtons.qcnt) navButtons.qcnt.textContent = String(queue().length);
  };

  const updateFilterRail = (): void => {
    for (const g of FILTERS) {
      for (const f of g.items) {
        const ref = filterButtons.get(f.id);
        if (!ref) continue;
        ref.count.textContent = String(countFor(f));
        ref.btn.classList.toggle('on', view.filter === f.id && !view.searchText);
      }
    }
    overlayButtons.owned?.classList.toggle('on', view.filterOwned);
    overlayButtons.craftable?.classList.toggle('on', view.filterCraftable);
  };

  const syncSearch = (): void => {
    updateFilterRail();
    const title = el('listTitle');
    if (title) title.textContent = view.searchText ? `Search: "${view.searchText}"` : filterDef(view.filter).label;
    const arr = visibleNodes();
    const count = el('listCount');
    if (count) count.textContent = `${arr.length} item${arr.length === 1 ? '' : 's'}`;
    const body = el('listBody');
    if (body) body.innerHTML = renderSearchList();
  };

  const syncDetail = (): void => {
    const center = el('paneCenter');
    if (center) center.innerHTML = renderCenterPane();
  };

  const syncCrafting = (): void => {
    const screen = el('craftingScreen');
    if (screen) screen.innerHTML = renderCraftingScreen();
  };

  const sync = (): void => {
    if (!root || !techIndex) return;
    updateNav();
    if (view.screen === 'search') syncSearch();
    else {
      updateFilterRail();
      if (view.screen === 'detail') syncDetail();
      else if (view.screen === 'crafting') syncCrafting();
    }
  };

  const setScreen = (screen: ScreenName): void => {
    view.screen = screen;
    applyScreenVisibility();
    sync();
    if (screen === 'detail') el('paneCenter')?.scrollTo({ top: 0 });
  };

  const selectItem = (id: string): void => {
    if (!node(id)) return;
    view.selected = id;
    setScreen('detail');
  };

  // ---- actions → socket ----
  const emit = (payload: StorageActionPayload): void => {
    getControllerNetwork()?.storageAction(payload);
  };
  const code = (): string | null => controllerState.currentRoomCode ?? null;
  const onAction = (act: string, id: string): void => {
    const c = code();
    if (!c) return;
    const n = node(id);
    const craftable = n ? isCraftable(n) : false;
    switch (act) {
      case 'craft1':
        emit({ code: c, action: 'craft_enqueue', id, quantity: 1 });
        showToast(craftable ? 'Crafting added to queue' : 'Not enough materials', craftable ? 'ok' : 'warn');
        break;
      case 'craft10':
        emit({ code: c, action: 'craft_enqueue', id, quantity: 10 });
        showToast(craftable ? 'Crafting added to queue' : 'Not enough materials', craftable ? 'ok' : 'warn');
        break;
      case 'auto': emit({ code: c, action: 'auto_toggle', id }); break;
      case 'dump1': emit({ code: c, action: 'dump', id, quantity: 1 }); break;
      case 'dump10': emit({ code: c, action: 'dump', id, quantity: 10 }); break;
      case 'dump100': emit({ code: c, action: 'dump', id, quantity: 100 }); break;
      case 'wares': emit({ code: c, action: 'wares_toggle', id }); break;
      case 'apply':
        emit({ code: c, action: 'apply_upgrade', id });
        showToast('Upgrade installed');
        break;
      case 'cancelAll': emit({ code: c, action: 'craft_cancel_all', id }); break;
      default: break;
    }
  };

  const onClick = (event: MouseEvent): void => {
    const target = event.target as HTMLElement | null;
    if (!target) return;
    const jobBtn = target.closest<HTMLElement>('[data-job]');
    if (jobBtn) {
      event.stopPropagation();
      const c = code();
      if (c) emit({ code: c, action: 'craft_cancel', jobId: jobBtn.dataset.job as string });
      return;
    }
    const autoBtn = target.closest<HTMLElement>('[data-auto]');
    if (autoBtn) {
      event.stopPropagation();
      const c = code();
      if (c) emit({ code: c, action: 'auto_toggle', id: autoBtn.dataset.auto as string });
      return;
    }
    const actBtn = target.closest<HTMLElement>('[data-act]');
    if (actBtn) {
      event.stopPropagation();
      onAction(actBtn.dataset.act as string, actBtn.dataset.id as string);
      return;
    }
    const goBtn = target.closest<HTMLElement>('[data-go]');
    if (goBtn) {
      selectItem(goBtn.dataset.go as string);
    }
  };

  const onSearchInput = (event: Event): void => {
    view.searchText = (event.target as HTMLInputElement).value;
    syncSearch();
    applyScreenVisibility();
  };

  const applyFilter = (id: string): void => {
    view.filter = id;
    view.searchText = '';
    const input = el('searchIn') as HTMLInputElement | null;
    if (input) input.value = '';
    setScreen('search');
  };

  // Build the filter rail (Library + Type) into the lower-left controller zone.
  const buildFilterRail = (): void => {
    if (!leftBottom) return;
    leftBottom.innerHTML = '';
    filterButtons.clear();
    const rail = document.createElement('div');
    rail.className = 'tech-rail';
    for (const g of FILTERS) {
      const sec = document.createElement('div');
      sec.className = 'tech-rail-sec';
      sec.textContent = g.grp;
      rail.appendChild(sec);
      for (const f of g.items) {
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'tech-rail-btn';
        const label = document.createElement('span');
        label.textContent = f.label;
        const count = document.createElement('span');
        count.className = 'tech-rail-count';
        count.textContent = String(countFor(f));
        btn.append(label, count);
        btn.addEventListener('click', () => applyFilter(f.id));
        rail.appendChild(btn);
        filterButtons.set(f.id, { btn, count });
      }
    }

    const makeOverlayBtn = (label: string): HTMLButtonElement => {
      const b = document.createElement('button');
      b.type = 'button';
      b.className = 'tech-rail-btn tech-rail-overlay';
      const dot = document.createElement('span');
      dot.className = 'tech-rail-dot';
      const lbl = document.createElement('span');
      lbl.textContent = label;
      b.append(dot, lbl);
      return b;
    };

    const overlaySec = document.createElement('div');
    overlaySec.className = 'tech-rail-sec';
    overlaySec.textContent = t('quick_filter');
    rail.insertBefore(overlaySec, rail.firstChild);

    const overlayRow = document.createElement('div');
    overlayRow.className = 'tech-rail-overlay-row';
    rail.insertBefore(overlayRow, overlaySec.nextSibling);

    const ownedBtn = makeOverlayBtn('Owned');
    ownedBtn.addEventListener('click', () => {
      view.filterOwned = !view.filterOwned;
      updateFilterRail();
      if (view.screen === 'search') syncSearch();
    });
    overlayRow.appendChild(ownedBtn);
    overlayButtons.owned = ownedBtn;

    const craftableBtn = makeOverlayBtn('Craftable');
    craftableBtn.addEventListener('click', () => {
      view.filterCraftable = !view.filterCraftable;
      updateFilterRail();
      if (view.screen === 'search') syncSearch();
    });
    overlayRow.appendChild(craftableBtn);
    overlayButtons.craftable = craftableBtn;

    leftBottom.appendChild(rail);
    updateFilterRail();
  };

  // Build the Search / Crafting nav buttons into the lower-right controller zone.
  const buildNavAux = (): void => {
    if (!rightAux) return;
    rightAux.innerHTML = '';
    const wrap = document.createElement('div');
    wrap.className = 'tech-nav';
    const searchBtn = document.createElement('button');
    searchBtn.type = 'button';
    searchBtn.className = 'tech-nav-btn';
    searchBtn.textContent = t('search');
    searchBtn.addEventListener('click', () => setScreen('search'));
    wrap.append(searchBtn);
    if (isOwned('unlock-processing')) {
      const craftBtn = document.createElement('button');
      craftBtn.type = 'button';
      craftBtn.className = 'tech-nav-btn';
      const craftLabel = document.createElement('span');
      craftLabel.textContent = t('crafting');
      const qcnt = document.createElement('span');
      qcnt.className = 'tech-nav-count';
      qcnt.textContent = String(queue().length);
      craftBtn.append(craftLabel, qcnt);
      craftBtn.addEventListener('click', () => setScreen('crafting'));
      wrap.append(craftBtn);
      navButtons.crafting = craftBtn;
      navButtons.qcnt = qcnt;
    }
    rightAux.appendChild(wrap);
    navButtons.search = searchBtn;
  };

  // ---- public lifecycle ----
  const render = (): void => {
    if (!controls) return;
    view.screen = 'search';
    if (!techIndex) {
      controls.innerHTML = '<div class="tech-room"><div class="hint" style="padding:24px">Loading tech directory…</div></div>';
      loadTechIndex(() => {
        if (controllerState.currentRole === 'storage') render();
      });
      return;
    }
    controls.classList.add('storage-role-layout');
    controls.innerHTML = shellHtml();
    root = controls.querySelector<HTMLDivElement>('.tech-room');
    root?.addEventListener('click', onClick);
    el('searchIn')?.addEventListener('input', onSearchInput);
    buildFilterRail();
    buildNavAux();
    applyScreenVisibility();
    sync();
  };

  const updateUi = (): void => {
    if (controllerState.currentRole !== 'storage') return;
    sync();
  };

  return { render, updateUi };
};
